import React from "react";
import { StyleguideDocument } from "tsi-common-react/src/styleguide/StyleguideDocument";

import { Context } from "@reactivated";

import { getDocument } from "@thelabnyc/thelabui/src/utils/dom";

import { Template as FinancingModal } from "../../templates/FinancingModalTemplate";
import { Template as SessionKeepAlive } from "../../templates/SessionKeepAliveTemplate";

type IProps = {
    storybook: boolean;
    children?: React.ReactNode;
};

/**
 * Tempurpedic's styleguide document chrome, shared by the block and element
 * fixture templates. Mirrors the legacy _styleguide_base.html: the
 * per-APP_SLUG skin stylesheets (screenshots run across the tempurpedic /
 * sale2 / *warranty subsites), the Open Sans font, the #root / #main-content
 * wrappers, and the keep-alive / financing furniture the production
 * PageSkeleton also mounts.
 */
export const StyleguideChrome = (props: IProps): React.JSX.Element => {
    const chrome = React.useContext(Context).chrome;
    // StyleguideDocument renders <html className="no-js"> (the no-JS fallback),
    // but the styleguide chrome -- unlike the production PageSkeleton -- has no
    // effect that drops it after hydration. Left in place, `no-js` both hides
    // lazyload <img> (`.no-js img.lazyload{display:none}`) and zeroes mobile
    // `.l-max-width` padding, and it blocks `whenHydrationSafe()` (which treats
    // no-js removal as the hydration-complete signal) from ever initialising
    // lazysizes / the lazy-background decorator. Drop it here post-hydration,
    // mirroring PageSkeleton, so the full-document styleguide render matches a
    // real page.
    React.useEffect(() => {
        getDocument()?.documentElement.classList.remove("no-js");
    }, []);
    return (
        <StyleguideDocument
            storybook={props.storybook}
            htmlLang={chrome.html_lang}
            bodyId={chrome.body_id}
            legacyJsUrl={`${chrome.static_base}dist2/legacy.js`}
            extraHead={
                <>
                    {chrome.skin_css_urls.map((url, i) => (
                        <link
                            key={i}
                            rel="stylesheet"
                            type="text/css"
                            href={url}
                        />
                    ))}
                    <link
                        rel="stylesheet"
                        type="text/css"
                        href="//fonts.googleapis.com/css?family=Open+Sans"
                    />
                </>
            }
            bodyEnd={
                <>
                    <SessionKeepAlive />
                    <FinancingModal />
                </>
            }
        >
            {props.storybook ? (
                props.children
            ) : (
                <div id="root">
                    <main id="main-content">{props.children}</main>
                </div>
            )}
        </StyleguideDocument>
    );
};
